import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Coffee,
  ChevronDown,
  FileUp,
  Loader2,
  LogOut,
  Pause,
  UserCheck,
  Utensils,
  Clock3,
} from 'lucide-react';
import { API_BASE_URL } from '../apiBase';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import './AttendantClockBar.css';

const WORK_STATUSES = [
  { id: 'available', icon: UserCheck, tone: 'ok' },
];

const PAUSE_STATUSES = [
  { id: 'lunch', icon: Utensils, tone: 'pause' },
  { id: 'break', icon: Coffee, tone: 'pause' },
  { id: 'unavailable', icon: Pause, tone: 'pause' },
];

const END_STATUSES = [
  { id: 'off', icon: LogOut, tone: 'off' },
];

function toneFor(status) {
  if (status === 'available') return 'ok';
  if (status === 'sick') return 'sick';
  if (status === 'off' || !status) return 'off';
  return 'pause';
}

function formatElapsed(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m} min`;
}

/**
 * Disponibilidade operacional do atendente. Não substitui um relógio de ponto trabalhista.
 */
export default function AttendantClockBar({ className, compact = false }) {
  const { isAttendant, user } = useAuth();
  const { t } = useLanguage();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [nowTick, setNowTick] = useState(0);
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const fileRef = useRef(null);
  const barRef = useRef(null);
  const toggleRef = useRef(null);

  useEffect(() => {
    if (!compact || !mobileExpanded) return undefined;
    const outside = event => {
      if (!barRef.current?.contains(event.target)) setMobileExpanded(false);
    };
    const escape = event => {
      if (event.key === 'Escape') {
        setMobileExpanded(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [compact, mobileExpanded]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/team/attendance/me`, {
        withCredentials: true,
      });
      setState(data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!isAttendant) return undefined;
    load();
    const timer = setInterval(load, 60000);
    const tick = setInterval(() => setNowTick((n) => n + 1), 30000);
    return () => {
      clearInterval(timer);
      clearInterval(tick);
    };
  }, [isAttendant, load]);

  useEffect(() => {
    if (!isAttendant || !state?.status) return;
    window.dispatchEvent(
      new CustomEvent('aurax:attendance-status', { detail: { status: state.status } }),
    );
  }, [isAttendant, state?.status]);

  const current = state?.status || 'off';
  const tone = toneFor(current);
  const statusLabel = t(`acb.status.${current}`) || t('acb.offDuty');
  const elapsed = useMemo(
    () => formatElapsed(state?.status_at) || formatElapsed(state?.shift_started_at),
    // nowTick força atualização periódica
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state?.status_at, state?.shift_started_at, nowTick],
  );
  const displayName = state?.name || user?.name || t('acb.roleFallback');
  const needsNote = ['unavailable', 'sick'].includes(current);

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/team/attendance/clock`,
        { status, note: note.trim() || undefined },
        { withCredentials: true },
      );
      setState(data);
      setNote('');
      if (compact) setMobileExpanded(false);
      const label = t(`acb.status.${status}`) || data.status_label || status;
      toast.success(t('acb.statusToast', { label }));
    } catch (e) {
      const http = e?.response?.status;
      const d = e?.response?.data?.detail;
      let msg =
        typeof d === 'string'
          ? d
          : Array.isArray(d)
            ? d.map((x) => x?.msg || JSON.stringify(x)).join(', ')
            : d && typeof d === 'object'
              ? d.message || d.msg || JSON.stringify(d)
              : null;
      if (!msg) {
        if (!e?.response) msg = t('acb.errNetwork');
        else if (http === 401) msg = t('acb.errSession');
        else if (http === 403) msg = t('acb.errForbidden');
        else if (http) msg = t('acb.errHttp', { code: http });
        else msg = t('acb.errGeneric');
      }
      toast.error(msg);
      console.error('clock error', http, e?.response?.data || e);
    } finally {
      setBusy(false);
    }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('kind', 'medical');
      form.append('note', note.trim() || t('acb.docNote'));
      await axios.post(`${API_BASE_URL}/api/team/attendance/documents`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('acb.docOk'));
      await setStatus('sick');
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('acb.docFail'));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  if (!isAttendant) return null;

  const renderChip = (s) => {
    const Icon = s.icon;
    const active = current === s.id;
    return (
      <button
        key={s.id}
        type="button"
        disabled={busy}
        aria-pressed={active}
        className={cn('acb-chip', `acb-chip--${s.tone}`, active && 'is-active')}
        onClick={() => setStatus(s.id)}
      >
        <Icon className="acb-chip-icon" strokeWidth={2.2} aria-hidden />
        <span>{t(`acb.status.${s.id}`)}</span>
      </button>
    );
  };

  return (
    <section
      ref={barRef}
      className={cn('acb', `acb--${tone}`, compact && 'acb--compact', mobileExpanded && 'acb--mobile-open', className)}
      data-testid="attendant-clock-bar"
      aria-label={t('acb.aria')}
    >
      <div className="acb-identity">
        <div className={cn('acb-dot', `acb-dot--${tone}`)} aria-hidden />
        <div className="acb-identity-copy">
          <p className="acb-kicker">{t('acb.kicker')}</p>
          <p className="acb-title">
            <span className="acb-status">{statusLabel}</span>
            <span className="acb-sep" aria-hidden>
              ·
            </span>
            <span className="acb-name">{displayName}</span>
          </p>
          {elapsed ? (
            <p className="acb-meta">
              <Clock3 className="acb-meta-icon" aria-hidden />
              {t('acb.elapsed', { elapsed })}
            </p>
          ) : (
            <p className="acb-meta">{t('acb.pickStatus')}</p>
          )}
        </div>
        <button
          type="button"
          ref={toggleRef}
          className="acb-mobile-toggle"
          aria-expanded={mobileExpanded}
          aria-controls="attendant-clock-controls"
          onClick={() => setMobileExpanded((open) => !open)}
        >
          <span>{mobileExpanded ? 'Fechar' : 'Alterar status'}</span>
          <ChevronDown className="acb-mobile-toggle-icon" aria-hidden />
        </button>
      </div>

      <div id="attendant-clock-controls" className="acb-disclosure-panel" hidden={compact && !mobileExpanded}>
      <div className="acb-controls" role="group" aria-label={t('acb.statusAria')}>
        <div className="acb-group">{WORK_STATUSES.map(renderChip)}</div>
        <div className="acb-divider" aria-hidden />
        <div className="acb-group acb-group--wrap">{PAUSE_STATUSES.map(renderChip)}</div>
        <div className="acb-divider" aria-hidden />
        <div className="acb-group">{END_STATUSES.map(renderChip)}</div>
      </div>

      <div className="acb-aside">
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          accept="image/*,.pdf,audio/*,video/*,.doc,.docx"
          onChange={(e) => uploadDoc(e.target.files?.[0])}
        />
        <p className="acb-guidance">{t('acb.guidance')}</p>
        <details className="acb-more">
          <summary>
            <span>{t('acb.more')}</span>
            <ChevronDown className="acb-more-icon" aria-hidden />
          </summary>
          <div className="acb-more-body">
            <input
              className={cn('acb-note', needsNote && 'is-emphasized')}
              placeholder={t('acb.noteOptional')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              aria-label={t('acb.noteAria')}
            />
            <p className="acb-privacy">{t('acb.notePrivacy')}</p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              className="acb-doc-btn"
              onClick={() => fileRef.current?.click()}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5" />}
              {t('acb.uploadSick')}
            </Button>
          </div>
        </details>
      </div>
      </div>
    </section>
  );
}
